import { loadStripe } from '@stripe/stripe-js';
import { MetodoPagamentoVendaEnum } from '../types/enums/metodo-pagamento.enum';
import {
  MoedaVendaDescricao,
  MoedaVendaEnum,
} from '../types/enums/moeda-venda.enum';
import { Checkout } from '../types/checkout';
import { CreatePurchaseContract } from '../types/create-purchase-contract';
import { removeMask } from '../utils/remove-mask';
import { getAllUrlParams, getUrlParams } from '../utils/get-url-params';
import { Api } from '../hooks/useApi';
import { awaitEmitUtmifyEvent, emitUtmifyEvent } from '../utils/emitUtmifyEvent';
import { getStripePublicKey } from '../utils/stripe-public-key';

type GooglePayCtx = {
  data: CreatePurchaseContract;
  checkout: Checkout;
  id?: string;
  api: Api;
  gerarTokenRecaptcha: (path: string) => Promise<string>;
  navigate: (path: string, options?: any) => void;
  location: any;
  getUrlParams: typeof getUrlParams;
  paymentMethod: MetodoPagamentoVendaEnum;
  alert: any;
  dispatch: any;
};

export const googlePayStrategy = async (rawCtx: any): Promise<void> => {
  const ctx = rawCtx as GooglePayCtx;
  const { data, checkout, api, alert, navigate, gerarTokenRecaptcha, id } = ctx;
  const afiliadoId = getUrlParams('affiliate') ?? undefined;
  try {
    const recaptchaToken = await gerarTokenRecaptcha('/venda');

    const payload = {
      metodoPagamento: MetodoPagamentoVendaEnum.GOOGLE_PAY,
      produtoId: id ?? '',
      emailComprador: data.email,
      nomeComprador: data.fullName,
      sobrenomeComprador: data.fullName,
      cpfComprador: removeMask(data.cpf),
      telefoneComprador: removeMask(data.phoneNumber),
      checkoutId: checkout?.id,
      recaptchaToken,
      postalCode: data?.postalCode,
      state: data?.state,
      city: data?.city,
      district: data?.neighborhood,
      address: data?.address,
      number: data?.number,
      complement: data?.complement,
      afiliadoId,
      urlParams: getAllUrlParams(),
    };

    const res = await api.Venda.createPix(payload);

    const venda = res.data;
    emitUtmifyEvent({
      event: 'waiting_payment',
      vendaId: venda?.id,
    });

    const clientSecret: string | undefined = venda?.clientSecret;
    if (!clientSecret) {
      alert.show('Não foi possível iniciar o pagamento com Google Pay.');
      return;
    }

    const stripe = await loadStripe(getStripePublicKey(checkout));
    if (!stripe) {
      alert.show('Stripe não foi carregado corretamente.');
      return;
    }

    const amountInCents = Math.round((checkout.subtotal ?? 0) * 100);
    const country = checkout.pais;
    const currency =
      MoedaVendaDescricao[checkout.moeda ?? MoedaVendaEnum.BRL].toLowerCase();

    const paymentRequest = stripe.paymentRequest({
      country,
      currency,
      total: {
        label: checkout?.produto?.nome ?? 'Order',
        amount: amountInCents,
      },
      requestPayerName: false,
      requestPayerEmail: false,
    });

    const canPay = await paymentRequest.canMakePayment();

    if (!canPay || !canPay.googlePay) {
      alert.show(
        'Google Pay não está disponível neste dispositivo ou navegador.',
      );
      return;
    }

    paymentRequest.on('paymentmethod', async (ev) => {
      try {
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          {
            payment_method: ev.paymentMethod.id,
          },
          { handleActions: true },
        );

        if (error) {
          ev.complete('fail');
          alert.show(
            error.message || 'Erro ao processar pagamento com Google Pay.',
          );
          return;
        }

        if (paymentIntent && paymentIntent.status === 'requires_action') {
          const { error: actionError, paymentIntent: finalIntent } =
            await stripe.confirmCardPayment(clientSecret);

          if (actionError || finalIntent?.status !== 'succeeded') {
            ev.complete('fail');
            alert.show(
              actionError?.message ||
                'Não foi possível concluir o pagamento com Google Pay.',
            );
            return;
          }

          ev.complete('success');
        } else if (paymentIntent?.status === 'succeeded') {
          ev.complete('success');
        } else {
          ev.complete('fail');
          alert.show('Não foi possível concluir o pagamento com Google Pay.');
          return;
        }

        if (venda?.id) {
          await awaitEmitUtmifyEvent({ event: 'paid', vendaId: venda.id });
        }

        if (checkout?.backRedirect) {
          window.location.href = checkout.backRedirect;
        } else {
          navigate(`/obrigado/${checkout.id}?vendaId=${venda.id}`);
        }
      } catch (err: any) {
        console.error(err);
        ev.complete('fail');
        alert.show(
          err?.message ||
            'Erro inesperado ao processar Google Pay. Tente novamente.',
        );
      }
    });

    await paymentRequest.show();
  } catch (err: any) {
    console.error(err);
    alert.show(
      err?.message ||
        'Erro inesperado ao iniciar o fluxo de pagamento com Google Pay.',
    );
  }
};
